/**
 * Shared Language Constants
 *
 * Canonical list of supported languages and helper functions used across
 * ChartMind (useLanguageSettings, useVoiceRecording), SymptomChecker
 * (voice-conversation/constants), and the translation service.
 *
 * This is the SINGLE SOURCE OF TRUTH for language definitions. When adding
 * a new language, update only this file and all consumers will pick it up.
 */

/**
 * Supported languages for speech recognition, translation, and UI.
 *
 * Shape includes STT provider metadata so consumers can derive everything
 * they need from a single array:
 *
 * - code:        BCP-47 language code (used for STT, TTS, and translation)
 * - label:       Human-readable language name
 * - flag:        Emoji flag for the language
 * - sttProvider: Which STT backend to use ('deepgram' | 'google')
 * - sttModel:    Deepgram model name ('flux' | 'nova-3') or null for non-Deepgram
 * - useFlux:     Whether to use Deepgram Flux end-of-turn detection
 * - deepgramLang: Deepgram language code (e.g. 'en', 'es') or null for non-Deepgram
 */
export const SUPPORTED_LANGUAGES = [
  {
    code: 'en-US',
    label: 'English',
    flag: '🇺🇸',
    sttProvider: 'deepgram',
    sttModel: 'nova-3',
    useFlux: false, // Deepgram Flux WebSocket disabled; use timer-based pause detection
    deepgramLang: 'en',
  },
  {
    code: 'es-ES',
    label: 'Spanish',
    flag: '🇪🇸',
    sttProvider: 'deepgram',
    sttModel: 'nova-3',
    useFlux: false,
    deepgramLang: 'es',
  },
  {
    code: 'fr-FR',
    label: 'French',
    flag: '🇫🇷',
    sttProvider: 'deepgram',
    sttModel: 'nova-3',
    useFlux: false,
    deepgramLang: 'fr',
  },
  {
    code: 'sw-KE',
    label: 'Swahili',
    flag: '🇰🇪',
    sttProvider: 'google',
    sttModel: null,
    useFlux: false,
    deepgramLang: null,
  },
];

/**
 * Get full language info by BCP-47 code.
 * Falls back to the first entry (English) if code is unknown.
 */
export const getLanguageByCode = (code) => {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code) || SUPPORTED_LANGUAGES[0];
};

/**
 * Get a human-readable language name from a BCP-47 code.
 * Replaces the old LANGUAGE_NAMES dict in translationService.js.
 */
export const getLanguageName = (code) => {
  return getLanguageByCode(code).label;
};

/**
 * Check if a language requires Google Cloud STT (not supported by Deepgram).
 */
export const requiresGoogleSTT = (languageCode) => {
  const lang = getLanguageByCode(languageCode);
  return lang.sttProvider === 'google';
};

/**
 * Check if a language should use Deepgram Flux (model-integrated turn detection).
 * Currently English only.
 */
export const shouldUseFlux = (languageCode) => {
  const lang = getLanguageByCode(languageCode);
  return lang.useFlux === true;
};

/**
 * Get the Deepgram model for a language ('flux', 'nova-3', or null for non-Deepgram).
 */
export const getDeepgramModel = (languageCode) => {
  const lang = getLanguageByCode(languageCode);
  return lang.sttModel;
};

/**
 * Get the Deepgram language code (e.g. 'en', 'es', 'fr') or null for non-Deepgram.
 */
export const getDeepgramLanguageCode = (languageCode) => {
  const lang = getLanguageByCode(languageCode);
  return lang.deepgramLang;
};
